import { useEffect, useState } from "react";
import { BiEditAlt, BiTrash } from "react-icons/bi";
import { FaPlus } from "react-icons/fa";
import { useDispatch } from "react-redux";
import { storageKeys } from "../../Constants/storage.key";
import { driverService } from "../../Services/driver.service";
import { storage } from "../../Services/storage.service";
import { toDateString, generateGUID } from "../../Services/string.service";
import DeleteModal from "../Components/Modals/Delete-modal";
import Spinner from "../Components/Spinner";
import { ToastComponent } from "../Components/ToastComponent";
import { IoCarOutline } from "react-icons/io5";
import DriverRegister from "./DriverRegister";
import DriverProfile from "./DriverProfile";
import { IoMdArrowBack } from "react-icons/io";
import { useTranslation } from "react-i18next";
import { useRef } from "react";
import { fileService } from "../../Services/file.service";
import { Modal, Button } from "react-bootstrap";

export default function DriverList() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  dispatch({ type: "title", value: t("TITLE.DRIVERS_REGISTERED") });

  const [toastParams, setToastParams] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [data, setData] = useState([]);
  const fileInputRef = useRef(null);

  const [isAddEditing, setIsAddEditing] = useState({
    show: false,
    driverId: 0,
    name: null,
    document: null,
    functionalRegistry: null,
    iButtonKey: null,
    licenseNumber: null,
    licenseCategory: null,
    licenseExpiration: null,
    birthDate: null,
    mobilePhone: null,
  });
  const [isDeleting, setIsDeleting] = useState({
    show: false,
    itemName: "",
    itemRemoveId: 0,
  });
  const [isLinking, setIsLinking] = useState({
    show: false,
    driverId: 0,
    driverName: "",
    vehicles: [],
  });
  const [showImportModal, setShowImportModal] = useState(false);
  const [uploadsData, setUploadsData] = useState([]);

  const onLink = async (item) => {
    onCloseModal();
    if (item) {
      try {
        setIsLoading(true);
        const data = await driverService.fetchlinkedVehicleToDriver(
          item.driverId
        );
        setIsLinking({
          show: true,
          driverId: item.driverId,
          driverName: item.name,
          vehicles: data,
        });
      } catch (error) {
        setToastParams(
          JSON.stringify({
            show: true,
            title: t("COMMON.ERROR"),
            body: t("ERRORS.FETCH_DRIVER_LIST_ERROR"),
            color: "Danger",
          })
        );
      } finally {
        setIsLoading(false);
      }
    } else {
    }
  };

  const onNew = (item) => {
    onCloseModal();
    if (item) {
      setIsAddEditing({
        show: true,
        driverId: item.driverId,
        name: item.name,
        document: item.document,
        functionalRegistry: item.functionalRegistry,
        iButtonKey: item.iButtonKey,
        licenseNumber: item.licenseNumber,
        licenseCategory: item.licenseCategory,
        licenseExpiration: item.licenseExpiration,
        birthDate: item.birthDate,
        mobilePhone: item.mobilePhone,
      });
    } else {
      setIsAddEditing({
        show: true,
        driverId: 0,
        name: null,
        document: null,
        functionalRegistry: null,
        iButtonKey: null,
        licenseNumber: null,
        licenseCategory: null,
        licenseExpiration: null,
        birthDate: null,
        mobilePhone: null,
      });
    }
  };

  const onCloseModal = async () => {
    setIsAddEditing({
      show: false,
      driverId: 0,
      name: null,
      document: null,
      functionalRegistry: null,
      iButtonKey: null,
      licenseNumber: null,
      licenseCategory: null,
      licenseExpiration: null,
      birthDate: null,
      mobilePhone: null,
    });

    setIsLinking({
      driverId: 0,
      show: false,
      vehicles: [],
    });

    setTimeout(() => {
      initialize(true);
    }, 500);
  };

  const onDelete = (driverId) => {
    if (!driverId) return;
    try {
      setIsLoading(true);
      driverService.deleteDriver(driverId);
    } catch (error) {
      setToastParams(
        JSON.stringify({
          show: true,
          title: t("COMMON.ERROR"),
          body: t("ERRORS.DELETE_DRIVER_ERROR"),
          color: "Danger",
        })
      );
    } finally {
      setIsLoading(false);

      setTimeout(() => {
        initialize(true);
      }, 500);
    }
  };

  const handleExportDrivers = async () => {
    try {
      setIsLoading(true);
      const blob = await driverService.exportDriver();
      
      if (!blob) {
        setToastParams(
          JSON.stringify({
            show: true,
            title: t("COMMON.ERROR"),
            body: t("ERRORS.EXPORT_DRIVER_ERROR"),
            color: "Danger",
          })
        );
        return;
      }

      const guid = generateGUID();
      const fileName = `drivers_${guid}.xlsx`;
      
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      setToastParams(
        JSON.stringify({
          show: true,
          title: t("COMMON.ERROR"),
          body: t("ERRORS.EXPORT_DRIVER_ERROR"),
          color: "Danger",
        })
      );
    } finally {
      setIsLoading(false);
    }
  };

  const generateGUID = () => {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
      const r = Math.random() * 16 | 0;
      const v = c === 'x' ? r : (r & 0x3 | 0x8);
      return v.toString(16);
    });
  };

  const openImportDialog = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleImportFileChange = async (event) => {
    const file = event.target.files?.[0];

    if (!file) return;

    try {
      setIsLoading(true);

      const response = await driverService.importDriver(file);

      if (response) {
        setToastParams(
          JSON.stringify({
            show: true,
            title: t("COMMON.SUCCESS"),
            body: t("DRIVER.IMPORT_SUCCESS"),
            color: "Success",
          })
        );

        event.target.value = "";
        initialize(true);
        fetchUploads();
      } else {
        setToastParams(
          JSON.stringify({
            show: true,
            title: t("COMMON.ERROR"),
            body: t("ERRORS.IMPORT_DRIVER_ERROR"),
            color: "Danger",
          })
        );
        event.target.value = "";
        initialize(true);
      }
    } catch (error) {
      setToastParams(
        JSON.stringify({
          show: true,
          title: t("COMMON.ERROR"),
          body: t("ERRORS.IMPORT_DRIVER_ERROR"),
          color: "Danger",
        })
      );
    } finally {
      setIsLoading(false);
    }
  };

  const fetchUploads = async () => {
    try {
      const data = await driverService.uploads();
      setUploadsData(data || []);
    } catch (error) {
      console.error('Error fetching uploads:', error);
    }
  };

  useEffect(() => {
    let interval;
    if (showImportModal) {
      fetchUploads();
      interval = setInterval(fetchUploads, 10000);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [showImportModal]);



  const initialize = async (isUpdate) => {
    try {
      if (!isUpdate) setIsLoading(true);
      const userSessionData = storage.get(storageKeys.settings);
      const data = await driverService.fetchDriverList(
        userSessionData?.clientId
      );
      setData(data);
    } catch (error) {
      setToastParams(
        JSON.stringify({
          show: true,
          title: t("COMMON.ERROR"),
          body: t("ERRORS.FETCH_DRIVER_LIST_ERROR"),
          color: "Danger",
        })
      );
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    initialize();
  }, []);

  const columns = [
    t("DRIVER.NAME"),
    t("DRIVER.IBUTTON_KEY"),
    t("DRIVER.CNH_CATEGORY"),
  ];

  const fields = [
    "name",
    "iButtonKey",
    "licenseCategory",
  ];

  const renderData = (value, item, field, fieldsIndex) => {
    if (!field) return;

    if (
      field.toLowerCase().includes("date") ||
      field.toLowerCase().includes("expiration")
    ) {
      return <td key={fieldsIndex}>{value ? toDateString(value) : ""}</td>;
    }

    return <td key={fieldsIndex}>{value}</td>;
  };

  return (
    <div className="container-xxl flex-grow-1 container-p-y">
      <ToastComponent
        params={toastParams}
        closeToast={() => setToastParams(JSON.stringify({ show: false }))}
      />
      <DeleteModal
        itemName={isDeleting.itemName}
        itemRemoveId={isDeleting.itemRemoveId}
        callBackRemove={onDelete}
        show={isDeleting.show}
        handleClose={() =>
          setIsDeleting({ show: false, itemName: "", itemRemoveId: "" })
        }
      />

      <input
        type="file"
        accept=".csv, application/vnd.ms-excel, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
        ref={fileInputRef}
        style={{ display: "none" }}
        onChange={handleImportFileChange}
      />

      <Modal show={showImportModal} onHide={() => setShowImportModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Importações</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="d-flex gap-2 mb-3">
            <Button variant="success" onClick={() => { openImportDialog(); setShowImportModal(false); }}>
              Importar
            </Button>
            <Button variant="primary" onClick={() => { handleExportDrivers(); setShowImportModal(false); }}>
              Exportar
            </Button>
          </div>
          <div className="table-responsive">
            <table className="table table-striped">
              <thead>
                <tr>
                  <th>Arquivo</th>
                  <th>Status</th>
                  <th>Ativo</th>
                </tr>
              </thead>
              <tbody>
                {Array.isArray(uploadsData) && uploadsData.length > 0 ? (
                  uploadsData.map((upload, index) => (
                    <tr key={index}>
                      <td>{upload.eventTypeId ?? `Upload ${index + 1}`}</td>
                      <td>
                        <span className={`badge ${upload.completed ? 'bg-success' : 'bg-warning'}`}>
                          {upload.completed === "S" ? 'Concluído' : 'Pendente'}
                        </span>
                      </td>
                      <td>{upload.active === "S" ? 'Sim' : 'Não'}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="3" className="text-center text-muted">
                      Nenhum upload encontrado
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </Modal.Body>
      </Modal>

      {isLinking.show && (
        <>
          <div className="back-to-fleet" style={{ marginBottom: 8 }}>
            <button
              style={{ display: "flex", alignItems: "center" }}
              onClick={() => onCloseModal()}
            >
              <div className="icon-back-fleet">
                <IoMdArrowBack />
              </div>
              <span>{t("FLEET.BACK")}</span>
            </button>
          </div>
          <DriverProfile
            driverName={isLinking.driverName}
            driverId={isLinking.driverId}
            vehicles={isLinking.vehicles}
            handleClose={onCloseModal}
          />
        </>
      )}

      {isAddEditing.show && (
        <>
          <div className="back-to-fleet" style={{ marginBottom: 8 }}>
            <button
              style={{ display: "flex", alignItems: "center" }}
              onClick={() => onCloseModal()}
            >
              <div className="icon-back-fleet">
                <IoMdArrowBack />
              </div>
              <span>{t("FLEET.BACK")}</span>
            </button>
          </div>
          <DriverRegister driver={isAddEditing} handleClose={onCloseModal} />
        </>
      )}
      {isLoading ? (
        <Spinner />
      ) : (
        <>
          {!isAddEditing.show && !isLinking.show && (
            <div className="card">
              <div className="p-2 legend-item" >
                <button className="btn btn-tertiary" onClick={onNew}>
                  <FaPlus size={20} />
                </button>

                <button className="btn btn-primary" onClick={() => setShowImportModal(true)}>
                  Importações
                </button>
              </div>
              <div className="table-responsive text-nowrap flipped">
                <table className="table table-grid" id="paginated-table">
                  <thead>
                    <tr>
                      {columns.map((column, index) => (
                        <th key={index} style={{ width: 50 }}>
                          {column}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="table-border-bottom-0">
                    {data.map((item, index) => (
                      <tr key={index}>
                        {fields.map((field, fieldIndex) => (
                          <>
                            {renderData(item[field], item, field, fieldIndex)}
                          </>
                        ))}

                        <td>
                          <div className="col-4">
                            <IoCarOutline
                              className="icon-button"
                              onClick={() => onLink(item)}
                            />
                          </div>
                        </td>
                        <td>
                          <div className="col-4">
                            <BiEditAlt
                              className="icon-button"
                              onClick={() => onNew(item)}
                            />
                          </div>
                        </td>
                        <td>
                          <div className="col-6">
                            <BiTrash
                              className="icon-button"
                              onClick={() => {
                                setIsDeleting({
                                  show: true,
                                  itemName: item.name,
                                  itemRemoveId: item.driverId,
                                });
                              }}
                            />
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}